import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { calculatePos } from '@trello/arrays';
import { useUpdateBoardStarMutation } from './UpdateBoardStarMutation.generated';
import { memberId } from '@trello/session-cookie';
import { BoardTileDropdownWrapper } from 'app/src/components/BoardTile';
import { sendErrorEvent } from '@trello/error-reporting';
import { boardUrlFromShortLink } from '@trello/urls';
import isEqual from 'react-fast-compare';
import {
  DragAndDropStarredBoardsListItem,
  HiddenStarredBoardsListItem,
} from './DragAndDropStarredBoardsListItem';
import { BoardStarsQuery } from './BoardStarsQuery.generated';
import { StarredBoardsListPage } from './StarredBoardsListPage';
import { StarredBoardsListPageQuery } from './StarredBoardsListPageQuery.generated';
import { StarredBoardsMenuTestIds } from '@trello/test-ids';

type Board = NonNullable<StarredBoardsListPageQuery['boards']>[number];
type BoardStar = NonNullable<BoardStarsQuery['member']>['boardStars'][number];

export const STARRED_BOARDS_MENU_POPOVER_CLASSNAME =
  'starred-boards-menu-popover';

interface DragAndDropStarredBoardsListProps {
  boardStars: BoardStar[];
  pageSize: number;
}

export const DragAndDropStarredBoardsList: React.FC<DragAndDropStarredBoardsListProps> = ({
  boardStars: boardStarsFromCache,
  pageSize,
}) => {
  const [
    previousBoardStarsFromCache,
    setPreviousBoardStarsFromCache,
  ] = useState(boardStarsFromCache);
  const [boardStars, setBoardStars] = useState(boardStarsFromCache);
  const [portal, setPortal] = useState<Element | null>(null);
  const [updateBoardStar] = useUpdateBoardStarMutation();

  useEffect(() => {
    const cacheUpdate = !isEqual(
      boardStarsFromCache,
      previousBoardStarsFromCache,
    );
    if (cacheUpdate) {
      setPreviousBoardStarsFromCache(boardStarsFromCache);
      setBoardStars(boardStarsFromCache);
    }
  }, [boardStarsFromCache, previousBoardStarsFromCache]);

  const sortedBoardStars = useMemo(() => {
    return boardStars.slice().sort((a, b) => a.pos - b.pos);
  }, [boardStars]);

  const maxBoardStarPosition = useMemo(() => {
    return Math.max(...boardStars.map((b) => b.pos));
  }, [boardStars]);

  useEffect(() => {
    const portalEl = document.getElementsByClassName(
      STARRED_BOARDS_MENU_POPOVER_CLASSNAME,
    )[0];
    if (portalEl) {
      setPortal(portalEl);
    } else {
      sendErrorEvent(new Error('No portal for DragAndDropStarredBoardsList'), {
        tags: {
          feature: 'Starred Boards Menu Button',
          ownershipArea: 'trello-teamplates',
        },
      });
    }
  }, [setPortal]);

  // we disable drag and drop if there's no portal
  const disableDragAndDrop = !portal;

  interface UpdateBoardStarPosition {
    idBoard: string;
    position: number;
  }

  const updateBoardStarPosition = useCallback(
    async ({ idBoard, position }: UpdateBoardStarPosition) => {
      const boardStar = sortedBoardStars.find(
        (boardStar) => boardStar.idBoard === idBoard,
      );

      if (boardStar) {
        try {
          // calculatePos requires the boardStars to be passed in sorted order
          const newPos = calculatePos(position, sortedBoardStars, boardStar);

          const updatedBoardStars = sortedBoardStars.map((boardStar) => {
            const cloned = { ...boardStar };
            if (boardStar.idBoard === idBoard) {
              cloned.pos = newPos;
            }

            return cloned;
          });

          // update board order locally
          setBoardStars(updatedBoardStars);

          // update board order on server
          await updateBoardStar({
            variables: {
              memberId: memberId || '',
              boardStarId: boardStar.id,
              pos: newPos,
            },
          });
        } catch (err) {
          sendErrorEvent(err, {
            tags: {
              ownershipArea: 'trello-teamplates',
              feature: 'Starred Boards Menu Button',
            },
          });
          // there was an error, update board order to persistent version
          setBoardStars(boardStarsFromCache);
        }
      }
    },
    [updateBoardStar, sortedBoardStars, setBoardStars, boardStarsFromCache],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { draggableId, source, destination } = result;
      if (
        // they didn't drop it on a droppable
        !destination ||
        // they dropped it in the same position
        (destination.droppableId === source.droppableId &&
          destination.index === source.index)
      ) {
        return;
      }

      updateBoardStarPosition({
        idBoard: draggableId,
        position: destination.index,
      });
    },
    [updateBoardStarPosition],
  );

  const renderBoard = useCallback(
    (props: { board?: Board; boardStar: BoardStar }) => {
      const { board, boardStar } = props;

      const index = sortedBoardStars.findIndex(
        (star) => star.id === boardStar.id,
      );

      /**
       * react-beautiful-dnd requires the `index` props in a list of `Draggable`
       * components to be consecutive:
       *
       * https://github.com/atlassian/react-beautiful-dnd/blob/e17aa45700b210e91fdf25e60c702506b98dba13/docs/api/draggable.md#required-props
       *
       * This forces us to render a `Draggable` for each boardStar. We can't filter
       * the list prior to rendering, because we're only fetching the corresponding
       * board if the component is visible in the viewport, so we're not sure ahead
       * of time if the board is open or closed, or if we can't fetch the board at all
       * (permissions, API error, etc).
       *
       * To work around this, we render an empty, undraggable, hidden placeholder component.
       */
      if (!board || board.closed) {
        return (
          <HiddenStarredBoardsListItem
            boardId={boardStar.idBoard}
            index={index}
          />
        );
      }

      const parsedBoard = {
        ...board,
        url: boardUrlFromShortLink(board.shortLink, board.name),
        prefs: {
          background: board.prefs?.background || undefined,
          backgroundTile: board.prefs?.backgroundTile || undefined,
          backgroundImage: board.prefs?.backgroundImage || undefined,
          backgroundColor: board.prefs?.backgroundColor || undefined,
          backgroundImageScaled:
            board.prefs?.backgroundImageScaled || undefined,
          isTemplate: board.prefs?.isTemplate || undefined,
        },
        organization: {
          displayName: board.organization?.displayName || undefined,
        },
        dateLastView: board.dateLastView
          ? new Date(board.dateLastView)
          : undefined,
        dateLastActivity: board.dateLastActivity
          ? new Date(board.dateLastActivity)
          : undefined,
        idBoardStar: boardStar.id,
        pos: boardStar.pos,
      };

      return (
        <DragAndDropStarredBoardsListItem
          board={parsedBoard}
          disableDragAndDrop={disableDragAndDrop}
          index={index}
          maxBoardStarPosition={maxBoardStarPosition}
          portal={portal}
        />
      );
    },
    [disableDragAndDrop, maxBoardStarPosition, portal, sortedBoardStars],
  );

  const boardStarPages = useMemo(() => {
    const pagedArray = [];
    let pageNumber = 0;
    let offset = 0;

    while (offset < sortedBoardStars.length) {
      pagedArray[pageNumber] = {
        boardStars: sortedBoardStars.slice(offset, offset + pageSize),
      };
      pageNumber++;
      offset = offset + pageSize;
    }

    return pagedArray;
  }, [pageSize, sortedBoardStars]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="starred-boards-menu-popover"
        isDropDisabled={disableDragAndDrop}
      >
        {({ droppableProps, innerRef, placeholder }) => (
          <div
            {...droppableProps}
            ref={innerRef}
            data-test-id={StarredBoardsMenuTestIds.StarredBoardsMenuPopOver}
          >
            <BoardTileDropdownWrapper>
              {boardStarPages.map((page, pageNumber) => (
                <StarredBoardsListPage
                  key={pageNumber}
                  boardStars={page.boardStars}
                  renderBoard={renderBoard}
                />
              ))}
              {placeholder}
            </BoardTileDropdownWrapper>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
